import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ACADEMIES, ACADEMY_LABELS, type Academy, type ICourse, type IPage } from '@stem/contracts';
import { apiClient } from '../services/api';

/**
 * The catalogue.
 *
 * Deliberately *not* entitlement-gated. A free member sees every premium course
 * with a badge on it, because the outline's whole commercial model is a free
 * tier that shows you what the paid tier has. Hiding the shelf sells nothing;
 * the gate belongs on the lesson, not on the listing.
 */
export function CatalogPage() {
  const { orgSlug = '' } = useParams();
  const [academy, setAcademy] = useState<Academy | 'all'>('all');
  const [search, setSearch] = useState('');

  const filters = [
    'status eq published',
    academy === 'all' ? '' : `academy eq ${academy}`,
    search.trim() ? `title like ${search.trim()}` : '',
  ]
    .filter(Boolean)
    .join(' and ');

  const { data, isLoading } = useQuery({
    queryKey: ['courses', filters],
    queryFn: () =>
      apiClient.get<IPage<ICourse>>(
        `/v1/catalog/courses?filter=${encodeURIComponent(filters)}&sort=-updatedAt&limit=50`,
      ),
  });

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-ink-900">Courses</h1>
        <p className="text-sm text-ink-600">
          Continuing professional development, trainer certification, school leadership and the
          student learning centre.
        </p>
      </header>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`rounded-full px-3 py-1.5 text-xs font-medium ${
            academy === 'all' ? 'bg-brand-700 text-white' : 'bg-white text-ink-700 ring-1 ring-ink-200'
          }`}
          onClick={() => setAcademy('all')}
        >
          All
        </button>
        {ACADEMIES.map(option => (
          <button
            key={option}
            type="button"
            className={`rounded-full px-3 py-1.5 text-xs font-medium ${
              academy === option ? 'bg-brand-700 text-white' : 'bg-white text-ink-700 ring-1 ring-ink-200'
            }`}
            onClick={() => setAcademy(option)}
          >
            {ACADEMY_LABELS[option].split('—').pop()?.trim() ?? option}
          </button>
        ))}

        <input
          className="input ml-auto max-w-xs"
          placeholder="Search by title…"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>

      {isLoading && <p className="text-sm text-ink-500">Loading courses…</p>}

      {data && data.rows.length === 0 && (
        <div className="card text-sm text-ink-600">
          Nothing published here yet. An administrator can install a curriculum pack from{' '}
          <Link to={`/o/${orgSlug}/packs`} className="text-brand-700 underline">
            Curriculum packs
          </Link>
          .
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data?.rows.map(course => (
          <Link
            key={course.id}
            to={`/o/${orgSlug}/courses/${course.slug}`}
            className="card flex flex-col gap-2 transition-shadow hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-brand-700">
                {ACADEMY_LABELS[course.academy].split('—').pop()?.trim()}
              </span>
              <span className={course.accessTier === 'premium' ? 'badge-premium' : 'badge-free'}>
                {course.accessTier}
              </span>
            </div>
            <h2 className="text-base font-semibold text-ink-900">{course.title}</h2>
            <p className="line-clamp-3 text-sm text-ink-600">{course.summary}</p>
            <p className="mt-auto text-xs text-ink-500">
              {course.gradeBand ? `${course.gradeBand} · ` : ''}
              {course.estimatedMinutes > 0 ? `${course.estimatedMinutes} minutes` : 'Self-paced'}
            </p>
          </Link>
        ))}
      </div>

      {data && data.total > data.rows.length && (
        <p className="mt-4 text-xs text-ink-500">
          Showing {data.rows.length} of {data.total}.
        </p>
      )}
    </div>
  );
}
